import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import Attendance from './pages/Attendance';
import Leaves from './pages/Leaves';
import Payroll from './pages/Payroll';
import Profile from './pages/Profile';
import Onboarding from './pages/Onboarding';
import Approvals from './pages/Approvals';
import MyTeam from './pages/MyTeam';
import Reviews from './pages/Reviews';
import Announcements from './pages/Announcements';
import Documents from './pages/Documents';
import Recruitment from './pages/Recruitment';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import Reimbursements from './pages/Reimbursements';
import Issues from './pages/Issues';
import EmployeeDetail from './pages/EmployeeDetail';
import Departments from './pages/Departments';
import CustomRoles from './pages/CustomRoles';
import CompanyProfile from './pages/CompanyProfile';
import Branches from './pages/Branches';
import Reports from './pages/Reports';
import { PublicJobs, PublicJobDetail } from './pages/PublicJobs';
import PublicOffer from './pages/PublicOffer';
import Training from './pages/Training';
import Analytics from './pages/Analytics';
import Roles from './pages/Roles';
import AuditLogs from './pages/AuditLogs';
import Assets from './pages/Assets';
import Tickets from './pages/Tickets';
import TicketCategories from './pages/TicketCategories';
import EntraGovernance from './pages/EntraGovernance';
import AwsGovernance from './pages/AwsGovernance';
import EmployeeAccessRecords from './pages/EmployeeAccessRecords';
import ReportVault from './pages/ReportVault';
import SuperAdmin from './pages/SuperAdmin';
import SetupWizard from './pages/SetupWizard';
import BrandingProvider from './components/BrandingProvider';
import SetupGuard from './components/SetupGuard';
import './App.css';

function OnboardGuard({ children }) {
  const { user } = useAuth();
  // Force password change first
  if (user && user.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }
  // Employees (pure employees with no elevated role) must finish onboarding before full access
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isPureEmployee = userRoles.length > 0 && userRoles.every(r => r === 'employee');
  if (isPureEmployee && user?.onboarding_status !== 'verified') {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

const Wrap = ({ children }) => (
  <ProtectedRoute>
    <Layout>
      <OnboardGuard>{children}</OnboardGuard>
    </Layout>
  </ProtectedRoute>
);

const WrapRoles = ({ children, roles }) => (
  <ProtectedRoute roles={roles}>
    <Layout>{children}</Layout>
  </ProtectedRoute>
);

// Gates a route by the tenant's subscribed plan module(s).
// Renders an "upgrade" notice instead of the page when the module is locked.
const ModuleGate = ({ module, children }) => {
  const { user } = useAuth();
  const modules = user?.tenant?.modules || [];
  // If plan modules aren't loaded yet, allow render (avoid flash redirect).
  // Super admin and primary tenant admins also bypass to keep platform ops working.
  if (!modules.length) return children;
  if (modules.includes(module)) return children;
  return (
    <div className="max-w-2xl mx-auto mt-12 bg-white border border-amber-200 rounded-2xl p-8 shadow-sm text-center" data-testid="module-locked">
      <div className="text-4xl mb-3">🔒</div>
      <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Module not available on your plan</h2>
      <p className="text-slate-600">
        The <b>{module}</b> module isn't part of your <b>{user?.tenant?.plan_name || 'current'}</b> plan.
        Contact your platform admin to upgrade and unlock this feature.
      </p>
    </div>
  );
};

const WrapModule = ({ children, module, roles }) => (
  roles
    ? <WrapRoles roles={roles}><ModuleGate module={module}>{children}</ModuleGate></WrapRoles>
    : <Wrap><ModuleGate module={module}>{children}</ModuleGate></Wrap>
);

export default function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
      <BrowserRouter>
      <SetupGuard>
        <Routes>
          {/* Public */}
          <Route path="/jobs" element={<PublicJobs />} />
          <Route path="/jobs/:id" element={<PublicJobDetail />} />
          <Route path="/offer/:token" element={<PublicOffer />} />

          {/* Auth */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/change-password" element={
            <ProtectedRoute><ChangePassword /></ProtectedRoute>
          } />
          <Route path="/onboarding" element={
            <ProtectedRoute><Layout><Onboarding /></Layout></ProtectedRoute>
          } />

          {/* Authed */}
          <Route path="/dashboard" element={<Wrap><Dashboard /></Wrap>} />
          <Route path="/employees" element={<WrapRoles roles={['admin', 'hr', 'reporting_manager']}><Employees /></WrapRoles>} />
          <Route path="/employees/:id" element={<Wrap><EmployeeDetail /></Wrap>} />
          <Route path="/departments" element={<WrapRoles roles={['admin', 'hr']}><Departments /></WrapRoles>} />
          <Route path="/custom-roles" element={<WrapRoles roles={['admin', 'hr']}><CustomRoles /></WrapRoles>} />
          <Route path="/company-profile" element={<WrapRoles roles={['admin', 'hr']}><CompanyProfile /></WrapRoles>} />
          <Route path="/branches" element={<WrapRoles roles={['admin', 'hr']}><Branches /></WrapRoles>} />
          <Route path="/reports" element={<WrapModule roles={['admin', 'hr']} module="reports"><Reports /></WrapModule>} />
          <Route path="/analytics" element={<WrapModule roles={['admin', 'hr']} module="reports"><Analytics /></WrapModule>} />
          <Route path="/admin/roles" element={<WrapRoles roles={['admin']}><Roles /></WrapRoles>} />
          <Route path="/admin/audit-logs" element={<WrapRoles roles={['admin', 'it_admin']}><AuditLogs /></WrapRoles>} />
          <Route path="/assets" element={<WrapModule roles={['admin', 'it_admin']} module="assets"><Assets /></WrapModule>} />
          <Route path="/tickets" element={<WrapModule module="tickets"><Tickets /></WrapModule>} />
          <Route path="/tickets/:id" element={<WrapModule module="tickets"><Tickets /></WrapModule>} />
          <Route path="/admin/ticket-categories" element={<WrapModule roles={['admin']} module="tickets"><TicketCategories /></WrapModule>} />
          <Route path="/security" element={<Navigate to="/security/entra" replace />} />
          <Route path="/security/entra" element={<WrapModule roles={['admin', 'it_admin']} module="security"><EntraGovernance /></WrapModule>} />
          <Route path="/security/aws" element={<WrapModule roles={['admin', 'it_admin']} module="security"><AwsGovernance /></WrapModule>} />
          <Route path="/security/employee-access" element={<WrapModule roles={['admin', 'it_admin']} module="security"><EmployeeAccessRecords /></WrapModule>} />
          <Route path="/security/vault" element={<WrapModule roles={['admin', 'it_admin']} module="security"><ReportVault /></WrapModule>} />
          <Route path="/finops" element={<Navigate to="/security/entra" replace />} />
          <Route path="/training" element={<Wrap><Training /></Wrap>} />
          <Route path="/training/:mid" element={<Wrap><Training /></Wrap>} />
          <Route path="/approvals" element={<WrapRoles roles={['admin', 'hr']}><Approvals /></WrapRoles>} />
          <Route path="/my-team" element={<WrapRoles roles={['admin', 'reporting_manager']}><MyTeam /></WrapRoles>} />
          <Route path="/attendance" element={<Wrap><Attendance /></Wrap>} />
          <Route path="/leaves" element={<Wrap><Leaves /></Wrap>} />
          <Route path="/payroll" element={<WrapModule module="payroll"><Payroll /></WrapModule>} />
          <Route path="/reimbursements" element={<WrapModule module="payroll"><Reimbursements /></WrapModule>} />
          <Route path="/issues" element={<Wrap><Issues /></Wrap>} />
          <Route path="/reviews" element={<Wrap><Reviews /></Wrap>} />
          <Route path="/announcements" element={<Wrap><Announcements /></Wrap>} />
          <Route path="/documents" element={<Wrap><Documents /></Wrap>} />
          <Route path="/recruitment" element={<WrapRoles roles={['admin', 'hr']}><Recruitment /></WrapRoles>} />
          <Route path="/profile" element={
            <ProtectedRoute allowSuperAdmin><Layout><Profile /></Layout></ProtectedRoute>
          } />
          <Route path="/setup" element={
            <ProtectedRoute roles={['admin']}><SetupWizard /></ProtectedRoute>
          } />
          <Route path="/super-admin" element={
            <ProtectedRoute requireSuperAdmin><Layout><SuperAdmin /></Layout></ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </SetupGuard>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      </BrandingProvider>
    </AuthProvider>
  );
}
